/* ============================================================================
   RTB.a11y: the accessibility helpers the RTB behaviour scripts share.

   Load it with `defer` (classic script, not a module) from the head of
   layout/theme.liquid, on every page:
       <script src="{{ 'rtb-a11y.js' | asset_url }}" defer></script>

   Deferred scripts run in document order, so this one runs before every
   consumer: the head-loaded rtb-collection.js after it, and the
   section-loaded rtb-header.js and rtb-product.js further down the body. It
   defines no element and touches no markup, so a page without any consumer
   loses nothing.

   RELATIONSHIP TO HORIZON
   None. Horizon's modules have their own equivalents; RTB scripts are
   classic and cannot import them, so they share this namespace instead.

   WHAT IT PROVIDES (window.RTB.a11y)
     FOCUSABLE                   selector for keyboard-reachable controls
     isVisible(element)          rendered and not visibility:hidden
     trapFocus(container, event) keeps Tab / Shift+Tab inside a modal
     returnFocus(target, fallback)
                                 focuses where a modal was opened from, or
                                 the fallback when that is gone or <body>
     setDialog(element, label)   modal dialog semantics while open
     clearDialog(element)        removes them again
     announce(region, text)      writes a polite live-region message
     cancelAnnounce(region)      drops a pending write
     onSectionUnload(element, teardown)
                                 runs teardown when the theme editor unloads
                                 the element's section; returns the remover
   ============================================================================ */

(function () {
  'use strict';

  var RTB = (window.RTB = window.RTB || {});
  if (RTB.a11y) return;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Blank first, write on a later tick: the same string written twice in a
     row is otherwise announced only once. */
  var ANNOUNCE_DELAY = 60;
  var announceTimers = new WeakMap();

  /* Rendered and not visibility:hidden. `checkVisibility` also sees through a
     `display: none` ancestor such as a closed panel. */
  function isVisible(element) {
    if (typeof element.checkVisibility === 'function') {
      return element.checkVisibility({ visibilityProperty: true });
    }
    return element.offsetWidth > 0 || element.offsetHeight > 0;
  }

  /* Call from a Tab keydown while the modal is open. Wraps focus from the last
     visible control to the first and back; otherwise the browser moves it. */
  function trapFocus(container, event) {
    if (!container) return;

    var items = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(isVisible);
    if (items.length === 0) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Opening by pointer leaves <body> as the active element, whose focus() is
     a no-op, so anything not actually focusable falls back. */
  function returnFocus(target, fallback) {
    var usable =
      target &&
      target.isConnected &&
      target !== document.body &&
      typeof target.focus === 'function' &&
      isVisible(target);
    var next = usable ? target : fallback;
    if (next && typeof next.focus === 'function') next.focus();
  }

  /* Applied while open only: the RTB modals are the same element as a
     non-modal layout at another width, where role="dialog" would be false. */
  function setDialog(element, label) {
    if (!element) return;
    element.setAttribute('role', 'dialog');
    element.setAttribute('aria-modal', 'true');
    if (label) element.setAttribute('aria-label', label);
  }

  function clearDialog(element) {
    if (!element) return;
    element.removeAttribute('role');
    element.removeAttribute('aria-modal');
    element.removeAttribute('aria-label');
  }

  function cancelAnnounce(region) {
    if (!region) return;
    var timer = announceTimers.get(region);
    if (timer) window.clearTimeout(timer);
    announceTimers.delete(region);
  }

  function announce(region, text) {
    if (!region || !text) return;

    cancelAnnounce(region);
    region.textContent = '';
    announceTimers.set(
      region,
      window.setTimeout(function () {
        announceTimers.delete(region);
        if (region.isConnected) region.textContent = text;
      }, ANNOUNCE_DELAY)
    );
  }

  /* The editor can fire unload while the node is still attached, so the
     teardown it runs must be safe to run again from disconnectedCallback. */
  function onSectionUnload(element, teardown) {
    function handle(event) {
      var target = event && event.target;
      if (!(target instanceof Element) || !target.contains(element)) return;
      teardown();
    }

    document.addEventListener('shopify:section:unload', handle);
    return function () {
      document.removeEventListener('shopify:section:unload', handle);
    };
  }

  RTB.a11y = {
    FOCUSABLE: FOCUSABLE,
    isVisible: isVisible,
    trapFocus: trapFocus,
    returnFocus: returnFocus,
    setDialog: setDialog,
    clearDialog: clearDialog,
    announce: announce,
    cancelAnnounce: cancelAnnounce,
    onSectionUnload: onSectionUnload,
  };
})();
